<template>
  <section class="statistics-card">
    <div class="card-header">
      <h3 class="card-title">
        服务渠道平台会员增长趋势
      </h3>
      <div class="analyze-controls">
        <el-radio-group
          v-model="analyzeType"
          class="custom-radio-group"
        >
          <el-radio-button label="year">
            按年统计
          </el-radio-button>
          <el-radio-button label="quarter">
            按季统计
          </el-radio-button>
          <el-radio-button label="month">
            按月统计
          </el-radio-button>
        </el-radio-group>
        <div
          class="analyze-year-row"
          :class="{ 'is-placeholder': !(analyzeType === 'quarter' || analyzeType === 'month') }"
        >
          <el-date-picker
            v-model="analyzeYear"
            type="year"
            placeholder="选择年份"
            value-format="YYYY"
            class="analyze-year-picker"
            :clearable="false"
            :disabled-date="disabledAnalyzeYearDate"
            teleported
          />
        </div>
      </div>
    </div>
    <div
      ref="chartRef"
      class="trend-chart"
    />
  </section>
</template>

<script setup>
import {
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  shallowRef,
  useTemplateRef,
  watch
} from 'vue'
import * as echarts from 'echarts'
import { getServiceMemberGrowthTrend } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const SERIES = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    name: '直属事业单位及市级文艺家协会服务会员',
    color: '#6B4FF7'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    name: '区级文艺家协会服务会员',
    color: '#1478F2'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    name: '文艺两新组织服务会员',
    color: '#FFC51B'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    name: '对口帮扶地区组织服务会员',
    color: '#16C68B'
  }
]

const analyzeType = shallowRef('month')
const analyzeYear = shallowRef(String(new Date().getFullYear()))
const chartRef = useTemplateRef('chartRef')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let requestSequence = 0
let trendData = []

function disabledAnalyzeYearDate(time) {
  return time.getFullYear() > new Date().getFullYear()
}

function getLabels() {
  if (analyzeType.value === 'year') {
    const currentYear = new Date().getFullYear()
    const years = trendData
      .map(item => parseInt(String(item.timeLabel || '').replace('年', ''), 10))
      .filter(year => Number.isFinite(year))
      .sort((a, b) => a - b)
    const startYear = years.length > 0
      ? Math.min(years[0], currentYear - 9)
      : currentYear - 9
    const endYear = years.length > 0
      ? Math.max(years[years.length - 1], currentYear)
      : currentYear
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => `${startYear + index}年`
    )
  }
  if (analyzeType.value === 'quarter') {
    return ['第一季度', '第二季度', '第三季度', '第四季度']
  }
  return Array.from({ length: 12 }, (_, index) => `${index + 1}月`)
}

function getSeriesData(code, labels = getLabels()) {
  return labels.map((label) => {
    const row = trendData.find(item => item.timeLabel === label)
    const item = Array.isArray(row?.items)
      ? row.items.find(current => current.code === code)
      : null
    return Number(item?.value) || 0
  })
}

function getYAxisMax() {
  const maxValue = Math.max(
    ...SERIES.flatMap(item => getSeriesData(item.code)),
    0
  )
  if (maxValue <= 0) return 1
  const step = maxValue <= 10 ? 1 : maxValue <= 100 ? 10 : 100
  return Math.ceil(maxValue / step) * step
}

function buildOption() {
  const mobile = (chart?.getWidth() || 0) <= 680
  const labels = getLabels()
  return {
    color: SERIES.map(item => item.color),
    tooltip: {
      trigger: 'axis',
      confine: true,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      textStyle: { color: '#333' },
      axisPointer: {
        type: 'line',
        lineStyle: { color: '#aaa', type: 'dashed' }
      },
      formatter: (params) => {
        const rows = params
          .filter(item => item.value != null)
          .map(item => (
            `<div style="margin-top:9px">${item.marker}${item.seriesName}：${item.value} 个</div>`
          ))
          .join('')
        return `<strong>${params[0]?.axisValue || ''}</strong>${rows}`
      }
    },
    legend: {
      bottom: 2,
      left: 'center',
      type: mobile ? 'scroll' : 'plain',
      icon: 'circle',
      itemWidth: 11,
      itemHeight: 11,
      itemGap: mobile ? 14 : 32,
      textStyle: { color: '#444', fontSize: mobile ? 11 : 13 },
      data: SERIES.map(item => item.name)
    },
    grid: {
      left: 6,
      right: mobile ? 20 : 32,
      top: 14,
      bottom: mobile ? 78 : 58,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: labels,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#ddd' } },
      axisLabel: {
        color: '#555',
        fontSize: mobile ? 10 : 12,
        interval: mobile && analyzeType.value === 'month' ? 1 : 0
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: getYAxisMax(),
      splitNumber: 5,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#777', fontSize: 12 },
      splitLine: {
        lineStyle: {
          color: '#dfe3e8',
          type: 'dashed'
        }
      }
    },
    series: SERIES.map(item => ({
      name: item.name,
      type: 'line',
      symbol: 'circle',
      symbolSize: 7,
      showSymbol: false,
      smooth: false,
      connectNulls: false,
      lineStyle: {
        width: 2.5,
        color: item.color
      },
      itemStyle: {
        color: item.color,
        borderColor: '#fff',
        borderWidth: 2
      },
      emphasis: {
        focus: 'series',
        scale: 1.3
      },
      data: getSeriesData(item.code, labels)
    }))
  }
}

function renderChart() {
  chart?.setOption(buildOption(), { notMerge: true })
}

async function loadGrowthTrend() {
  if (!chart) return
  const sequence = ++requestSequence
  const needYear = analyzeType.value === 'quarter' || analyzeType.value === 'month'
  if (needYear && !analyzeYear.value) {
    analyzeYear.value = String(new Date().getFullYear())
  }
  try {
    const response = await getServiceMemberGrowthTrend({
      analyzeType: analyzeType.value,
      ...(needYear ? { analyzeYear: analyzeYear.value } : {})
    })
    if (sequence !== requestSequence) return
    trendData = response && response.code === 200 && Array.isArray(response.data)
      ? response.data
      : []
  } catch (error) {
    if (sequence !== requestSequence) return
    console.error('查询一级渠道服务会员增长趋势失败:', error)
    trendData = []
  }
  renderChart()
}

watch(analyzeType, (type) => {
  if (type === 'quarter' || type === 'month') {
    const currentYear = new Date().getFullYear()
    const year = parseInt(analyzeYear.value, 10)
    if (!Number.isFinite(year) || year > currentYear) {
      analyzeYear.value = String(currentYear)
    }
  }
  loadGrowthTrend()
})

watch(analyzeYear, () => {
  if (analyzeType.value === 'quarter' || analyzeType.value === 'month') {
    loadGrowthTrend()
  }
})

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadGrowthTrend()
})

onBeforeUnmount(() => {
  requestSequence++
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 18px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.card-title {
  min-width: 0;
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
}

.analyze-controls {
  display: flex;
  width: auto;
  min-width: 0;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.analyze-year-row {
  display: flex;
  width: 100%;
  height: 38px;
  justify-content: flex-start;

  &.is-placeholder {
    visibility: hidden;
    pointer-events: none;
  }

  :deep(.el-date-editor) {
    box-sizing: border-box;
    width: 100% !important;
  }
}

.analyze-year-picker {
  width: 100% !important;
}

.custom-radio-group {
  --el-radio-button-checked-bg-color: rgba(13, 98, 243, 0.1);
  --el-radio-button-checked-border-color: #0d62f3;
  --el-radio-button-checked-text-color: #0d62f3;

  :deep(.el-radio-button__inner) {
    padding: 8px 16px;
  }

  :deep(.el-radio-button.is-active .el-radio-button__inner) {
    border: 1px solid #0d62f3;
    background: rgba(13, 98, 243, 0.1);
    box-shadow: none;
    color: #0d62f3;
  }
}

.trend-chart {
  width: 100%;
  height: 440px;
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .analyze-controls,
  .custom-radio-group {
    width: 100%;
    align-items: stretch;
  }

  .custom-radio-group {
    :deep(.el-radio-button) {
      flex: 1 1 0;
    }

    :deep(.el-radio-button__inner) {
      width: 100%;
      padding: 8px 6px;
    }
  }

  .trend-chart {
    height: 410px;
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .custom-radio-group :deep(.el-radio-button__inner) {
    font-size: 12px;
  }

  .trend-chart {
    height: 390px;
  }
}
</style>
